import React, { useState, useRef, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Avatar, Dropdown, Modal, Radio, Space, Card, Spin, ConfigProvider, theme as antdTheme } from 'antd';
import {
  RobotOutlined,
  CommentOutlined,
  DatabaseOutlined,
  BookOutlined,
  HomeOutlined,
  LogoutOutlined,
  UserOutlined,
  TeamOutlined,
  AuditOutlined,
  SettingOutlined,
  FundViewOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
  BgColorsOutlined,
  GlobalOutlined,
  MessageOutlined,
  FundProjectionScreenOutlined,
  RightOutlined,
  CloseOutlined,
  MinusOutlined,
  BarChartOutlined,
  PlusOutlined,
  SlackOutlined,
} from '@ant-design/icons';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { ModelProvider } from './contexts/ModelContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DashboardProvider, useDashboard } from './contexts/DashboardContext';
import DashboardPanel from './components/DashboardPanel';
import ModelSelector from './components/ModelSelector';
import TokenQuotaWidget from './components/TokenQuotaWidget';
import AccessGate from './components/AccessGate';
import SessionExpiredModal from './components/SessionExpiredModal';
import { canAccess, HIDDEN_ROUTES } from './components/permissions';
import { getMyQuota } from './services/adminService';
import './App.css';

// Import pages
import Home from './pages/Home';
import ManagerChat from './pages/ManagerChat';
import ManagerQAHistory from './pages/ManagerQAHistory';
import DataChat from './pages/DataChat';
import DataGallery from './pages/DataGallery';
import DataExplorer from './pages/DataExplorer';
import Ontology from './pages/Ontology';
import DataModel from './pages/DataModel';

import GraphQLBuilder from './pages/GraphQLBuilder';
import KnowledgeChat from './pages/KnowledgeChat';
import KnowledgeQAHistory from './pages/KnowledgeQAHistory';
import KnowledgeStatus from './pages/KnowledgeStatus';

import UserManagement from './pages/UserManagement';
import DataAccessGroups from './pages/DataAccessGroups';
import AIUsage from './pages/AIUsage';
import DataUsage from './pages/DataUsage';
import UserActivity from './pages/UserActivity';
import UserSettings from './pages/UserSettings';
import AISettings from './pages/AISettings';
import DataAgentTelemetry from './pages/DataAgentTelemetry';
import Unauthorized from './pages/Unauthorized';
import EmailVerification from './pages/EmailVerification';

const { Content, Sider } = Layout;

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isCreatorOrAbove, logout, userEmail, userProfile, userRole } = useAuth();
  const { themes, currentTheme, changeTheme } = useTheme();
  const [showThemeModal, setShowThemeModal] = useState(false);
  const chatResetCallbacks = useRef({});
  const [quotaInfo, setQuotaInfo] = useState(null);

  // Fetch current user's quota
  const fetchQuota = useCallback(async () => {
    try {
      const q = await getMyQuota();
      setQuotaInfo(q);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchQuota();
    const interval = setInterval(fetchQuota, 60_000); // refresh every minute
    return () => clearInterval(interval);
  }, [fetchQuota]);

  // Define chat pages where New Chat button should appear
  const chatPages = ['/manager/chat', '/data/chat', '/knowledge/chat'];
  const isOnChatPage = chatPages.includes(location.pathname)
    || location.pathname === '/data/chat/dashboards'
    || location.pathname.startsWith('/data/chat/dashboard/');
  const isOnDataChat = location.pathname === '/data/chat'
    || location.pathname === '/data/chat/dashboards'
    || location.pathname.startsWith('/data/chat/dashboard/');

  // Dashboard state (only relevant for data/chat)
  const {
    isOpen: isDashboardOpen,
    isFullscreen: isDashboardFullscreen,
    toggleDashboard,
    globalDashboards,
    refreshGlobalDashboards,
    createAndOpenDashboard,
    createDraftDashboard,
    discardDraft,
    switchDashboard,
    setViewMode,
    openDashboardByUuid,
    openDashboard,
  } = useDashboard();

  // Refresh the sidebar REPORT submenu's global-dashboards list once on
  // mount (the context already does this on auth change; this re-runs
  // after a hot-reload or remount of the layout).
  useEffect(() => {
    refreshGlobalDashboards?.();
  }, [refreshGlobalDashboards]);

  // When the dashboard is in full-screen mode, the chat column becomes a
  // floating window in the bottom-right corner. The user can collapse it
  // to a circular FAB (and re-expand it) without unmounting the chat —
  // this preserves chat state and any in-flight streaming responses.
  const isFloatingChat = isOnDataChat && isDashboardFullscreen;
  // Default to COLLAPSED. Direct URL loads, refreshes, bookmarks and
  // sidebar menu clicks (CREATE / LIST / a specific dashboard) all land
  // the user in fullscreen with the chat as a small FAB so the
  // dashboard gets the full canvas. The chat window only auto-expands
  // when the user manually flips from hybrid → fullscreen via the
  // in-page fullscreen toggle (handled by the effect below).
  const [floatingChatExpanded, setFloatingChatExpanded] = useState(false);
  // Track the previous (open, fullscreen) pair so we can distinguish
  // "user clicked the fullscreen toggle while in hybrid" from "user
  // landed on a dashboard URL". Only the former should auto-expand
  // the floating chat — landing via URL/menu keeps it as a FAB.
  const prevDashStateRef = useRef({ isOpen: isDashboardOpen, isFullscreen: isDashboardFullscreen });
  useEffect(() => {
    const prev = prevDashStateRef.current;
    prevDashStateRef.current = { isOpen: isDashboardOpen, isFullscreen: isDashboardFullscreen };
    // Manual hybrid → fullscreen transition: panel was already open
    // AND not fullscreen, and now it is fullscreen. That's the only
    // path that should reveal the chat window automatically.
    if (prev.isOpen && !prev.isFullscreen && isDashboardOpen && isDashboardFullscreen) {
      setFloatingChatExpanded(true);
    }
    // Leaving fullscreen — collapse the floating-chat state so the
    // next entry into fullscreen starts fresh as a FAB. (When not in
    // fullscreen the flag is unobserved anyway.)
    if (!isDashboardFullscreen) {
      setFloatingChatExpanded(false);
    }
  }, [isDashboardOpen, isDashboardFullscreen]);

  // Drag-to-reposition for the floating chat. The chat window and the
  // collapsed FAB share a SINGLE position offset (anchored at the
  // bottom-right of the viewport) so toggling between expanded /
  // collapsed keeps the bottom-right corner pinned in the same spot —
  // dragging either control moves both. Persisted to localStorage so
  // the user's preferred placement survives reloads.
  const loadOffset = (key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return { x: 0, y: 0 };
      const v = JSON.parse(raw);
      return { x: Number(v.x) || 0, y: Number(v.y) || 0 };
    } catch { return { x: 0, y: 0 }; }
  };
  // Clamp an offset so the floating chat (or FAB) cannot be dragged
  // off-screen. The bottom-right anchor is `(24 + x, 24 + y)`; sizes
  // are 500 x 75vh for the expanded chat, which is the larger of the
  // two elements (the FAB is smaller, so this is a safe shared bound).
  const clampOffset = (offset) => {
    if (typeof window === 'undefined') return offset;
    const vw = window.innerWidth || 1024;
    const vh = window.innerHeight || 768;
    const W = 500;
    const H = vh * 0.75;
    const xMax = Math.max(-24, vw - 24 - W);
    const yMax = Math.max(-24, vh - 24 - H);
    return {
      x: Math.max(-24, Math.min(xMax, offset.x)),
      y: Math.max(-24, Math.min(yMax, offset.y)),
    };
  };
  const [chatOffset, setChatOffset] = useState(() => clampOffset(loadOffset('chatFloatingOffset')));
  const dragStateRef = useRef(null);

  // Re-clamp on viewport resize so a previously valid position can't
  // leave the window stranded off-screen after the user shrinks the
  // browser.
  useEffect(() => {
    const onResize = () => setChatOffset((prev) => clampOffset(prev));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const beginDrag = useCallback((e, kind) => {
    // For the chat header, ignore drags that originate on interactive
    // controls (e.g. the minimize button).
    if (e.target.closest('button') && kind === 'chat') return;
    e.preventDefault();
    dragStateRef.current = {
      kind,
      startX: e.clientX,
      startY: e.clientY,
      startOffsetX: chatOffset.x,
      startOffsetY: chatOffset.y,
      moved: false,
    };
    document.body.style.userSelect = 'none';
  }, [chatOffset]);

  useEffect(() => {
    const onMove = (e) => {
      const s = dragStateRef.current;
      if (!s) return;
      const dx = e.clientX - s.startX;
      const dy = e.clientY - s.startY;
      if (!s.moved && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) s.moved = true;
      // Anchor is bottom-right; moving the cursor right reduces "right",
      // moving down reduces "bottom". Hence subtract delta from offset.
      const next = { x: s.startOffsetX - dx, y: s.startOffsetY - dy };
      setChatOffset(clampOffset(next));
    };
    const onUp = () => {
      const s = dragStateRef.current;
      if (!s) return;
      // If the FAB was actually dragged, swallow the synthetic click that
      // follows on mouseup so it doesn't immediately re-expand the chat.
      if (s.kind === 'fab' && s.moved) {
        const swallow = (ev) => {
          ev.stopPropagation();
          ev.preventDefault();
          window.removeEventListener('click', swallow, true);
        };
        window.addEventListener('click', swallow, true);
      }
      dragStateRef.current = null;
      document.body.style.userSelect = '';
      try {
        localStorage.setItem('chatFloatingOffset', JSON.stringify(chatOffset));
      } catch { /* ignore */ }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [chatOffset]);

  // Register chat reset callback
  const registerChatReset = (path, callback) => {
    chatResetCallbacks.current[path] = callback;
  };

  // Handle New Chat button click
  const handleNewChat = () => {
    // Resolve the chat reset callback by the *base* chat path. When the user
    // is on a sub-route (e.g. /data/chat/dashboard/<uuid>) the registered key
    // is still /data/chat, so we strip the dashboard segment before lookup
    // and navigate back to the base chat path so the URL matches the reset.
    //
    // Preserve the current query string (notably ?mode=chat|hybrid|dashboard)
    // across the navigation — clicking "New Chat" should reset the
    // conversation but NOT change the page mode the user is currently in.
    const path = location.pathname;
    let basePath = path;
    if (path.startsWith('/data/chat/')) basePath = '/data/chat';
    const resetCallback = chatResetCallbacks.current[basePath];
    if (basePath !== path) {
      navigate({ pathname: basePath, search: location.search });
    }
    if (resetCallback) {
      resetCallback();
    }
  };

  // Build menu items with path property for navigation

  // ── Dashboard sidebar menu ("DASHBOARD" submenu) ────────────────
  // Adds a first-class navigation surface for dashboards alongside
  // DATA CHAT. CREATE persists immediately (so the resulting URL is
  // shareable); LIST opens the homepage view; up to 8 global dashboards
  // are surfaced as direct shortcuts.
  const goToChat = () => navigate('/data/chat?mode=chat');

  // All DASHBOARD-menu entry points (CREATE, LIST, specific global
  // dashboards) drive the URL into ?mode=dashboard so the panel opens
  // fullscreen with the floating chat. ?mode=chat is reserved for the
  // plain chat surface (no dashboard).
  //
  // We rely on the URL→state effect inside DashboardPanel to drive the
  // panel open/fullscreen. Calling `openDashboard()` here ourselves
  // would race the state→URL effect and rewrite our `?mode=dashboard`
  // back to `?mode=hybrid` mid-transition.
  // CREATE DASHBOARD: industrial "draft" UX — no DB write yet. The
  // dashboard gets persisted on the first action that needs an id
  // (rename, add a card, etc.). If the user never does one of those,
  // no orphan empty dashboard ever lands in the DB. This matches the
  // behavior of the "+ New Dashboard" button on the dashboard list
  // page (handleCreateDashboard in DashboardPanel.js).
  //
  // Drafts have no UUID, so the URL stays at /data/chat/dashboards
  // (the dashboard panel renders the active draft instead of the
  // homepage list when activeDashboardId === DRAFT_DASHBOARD_ID).
  const handleCreateReport = async () => {
    try {
      // Order matters: navigate FIRST so the DashboardPanel's
      // URL→state effect flips `isOpen` and `isFullscreen` together in
      // a single render. Doing createDraftDashboard() first would set
      // `isOpen=true` while `isFullscreen` was still false (an
      // intermediate hybrid state), and the floating-chat
      // auto-expand effect would then fire on the subsequent
      // hybrid→fullscreen transition — wrongly showing the chat
      // window when the user just opened CREATE from the chat page.
      navigate('/data/chat/dashboards?mode=dashboard');
      const draft = createDraftDashboard?.();
      if (!draft) { goToChat(); return; }
      await switchDashboard?.(draft.id);
      setViewMode?.('edit');
    } catch (err) {
      console.error('Failed to create draft dashboard:', err);
    }
  };

  const handleListReport = () => {
    // If the user is currently viewing a draft (CREATE DASHBOARD flow),
    // the URL is already `/data/chat/dashboards` so a plain navigate()
    // is a no-op and the draft surface stays put. Discarding the draft
    // releases the active id so the DashboardPanel re-shows the homepage
    // grid even when the URL doesn't change.
    discardDraft?.();
    // The DashboardPanel auto-shows the homepage view when the URL
    // is /data/chat/dashboards (and auto-opens the panel). The ?mode=
    // URL→state effect handles transitioning the panel into the
    // requested layout — we must not also poke `openDashboard()` here.
    navigate('/data/chat/dashboards?mode=dashboard');
  };

  const handleOpenReport = (uuid) => {
    if (!uuid) return;
    // Same draft-leak guard as handleListReport — make sure any active
    // draft is cleared so the URL→state path can take over cleanly.
    discardDraft?.();
    // Just navigate — DashboardPanel's URL→state effect will resolve
    // the uuid → dashboard id and open the panel. Calling
    // `openDashboardByUuid` here would race that effect and (on
    // failure) get redirected to the dashboard homepage URL.
    navigate(`/data/chat/dashboard/${uuid}?mode=dashboard`);
  };

  const reportChildren = [
    { key: 'report-hdr', type: 'group', label: 'DASHBOARD' },
    { key: 'report-create', label: 'CREATE DASHBOARD', onClick: handleCreateReport },
    { key: 'report-list',   label: 'LIST DASHBOARDS',  onClick: handleListReport },
  ];
  if (globalDashboards && globalDashboards.length > 0) {
    reportChildren.push({ key: 'report-divider-1', type: 'divider' });
    reportChildren.push({
      key: 'report-open',
      label: 'OPEN A DASHBOARD',
      popupClassName: 'sidebar-open-dashboard-popup',
      children: globalDashboards.map((d) => ({
        key: `report-${d.id}`,
        label: (d.name || 'Untitled').toUpperCase(),
        onClick: () => handleOpenReport(d.uuid),
      })),
    });
    reportChildren.push({ key: 'report-divider-2', type: 'divider' });
  }
  reportChildren.push({
    key: 'report-gallery',
    label: 'INSIGHT GALLERY',
    path: '/data-gallery',
  });

  const allMenuItems = [
    // DATA CHAT menu always lands on the plain chat surface — no
    // dashboard panel — by pinning ?mode=chat. Selection still matches
    // because getSelectedKey compares only the pathname portion.
    { key: '3-1', icon: <MessageOutlined />, label: 'DATA CHAT', path: '/data/chat?mode=chat' },
    {
      key: 'dashboard-menu',
      icon: <BarChartOutlined />,
      label: 'DASHBOARD',
      children: reportChildren,
    },
    {
      key: '3',
      icon: <DatabaseOutlined />,
      label: 'DATA',
      children: [
        { key: '3-hdr', type: 'group', label: 'DATA' },
        { key: '3-3', label: 'DATA CATALOG SYNC', path: '/data/catalog-sync' },
        { key: '3-4', label: 'DATA ONTOLOGY', path: '/data/ontology' },
        { key: '3-5', label: 'DATA MODEL', path: '/data/model' },
        { key: '3-6', label: 'DATA PORTAL', path: '/data/graphql-builder' },
      ],
    },
    { key: '5', icon: <TeamOutlined />, label: 'USER MANAGEMENT', path: '/admin/users' },
    {
      key: '6',
      icon: <AuditOutlined />,
      label: 'AUDIT',
      children: [
        { key: '6-hdr', type: 'group', label: 'AUDIT' },
        { key: '6-1', icon: <FundViewOutlined />, label: 'AI USAGE', path: '/admin/audit/ai' },
        { key: '6-2', icon: <DatabaseOutlined />, label: 'DATA USAGE', path: '/admin/audit/data' },
        { key: '6-3', icon: <TeamOutlined />, label: 'USER ACTIVITY', path: '/admin/audit/users' },
        { key: '6-4', icon: <DatabaseOutlined />, label: 'DATA AGENT TELEMETRY', path: '/admin/data-agent-telemetry' },
      ],
    },
    {
      key: '7',
      icon: <SettingOutlined />,
      label: 'SETTINGS',
      children: [
        { key: '7-hdr', type: 'group', label: 'SETTINGS' },
        { key: '7-1', icon: <SafetyOutlined />, label: 'USER SETTINGS', path: '/admin/settings/users' },
        { key: '7-2', icon: <ThunderboltOutlined />, label: 'AI SETTINGS', path: '/admin/settings/ai' },
        { key: '7-3', icon: <DatabaseOutlined />, label: 'DATA ACCESS GROUPS', path: '/admin/settings/data-access-groups' },
      ],
    },
    { key: 'divider-portal', type: 'divider' },
    { key: '1', icon: <HomeOutlined />, label: 'HOME', path: '/multi_agent' },
    {
      key: '2',
      icon: <CommentOutlined />,
      label: 'MANAGER',
      children: [
        { key: '2-hdr', type: 'group', label: 'MANAGER' },
        { key: '2-1', label: 'CHAT', path: '/manager/chat' },
        { key: '2-2', label: 'Q&A HISTORY', path: '/manager/history' },
      ],
    },
    {
      key: '4',
      icon: <BookOutlined />,
      label: 'KNOWLEDGE',
      children: [
        { key: '4-hdr', type: 'group', label: 'KNOWLEDGE' },
        { key: '4-1', label: 'CHAT', path: '/knowledge/chat' },
        { key: '4-2', label: 'Q&A HISTORY', path: '/knowledge/history' },
        { key: '4-3', label: 'KNOWLEDGE BASE', path: '/knowledge/status' },
      ],
    },
    {
      key: 'slack',
      icon: <SlackOutlined />,
      label: 'Contact us on Slack',
      onClick: () => window.open('https://app.slack.com/client/E03Q21ZUG2E/C0B6RNDMDSQ', '_blank', 'noopener,noreferrer'),
    },
  ];

  // Strip any query string before consulting the permissions table —
  // menu paths may include a default ?mode= (e.g. DATA CHAT) but the
  // permission table is keyed by pathname only.
  const pathnameOf = (p) => (typeof p === 'string' ? p.split('?')[0] : p);

  // Filter menu items based on permissions
  const menuItems = allMenuItems
    .map(item => {
      if (item.children) {
        const filtered = item.children.filter(child => {
          if (child.type === 'group' || child.type === 'divider') return true;
          // Submenu items (with their own children) are kept — their
          // own children are gated by their respective handlers.
          if (Array.isArray(child.children)) return true;
          // Items with onClick handlers (no path) are always shown —
          // permission gating happens inside the handler / target view.
          if (!child.path && typeof child.onClick === 'function') return true;
          return canAccess(userRole, pathnameOf(child.path));
        });
        // Keep only if there are real items (not just the group header)
        const hasRealItems = filtered.some(child => child.type !== 'group' && child.type !== 'divider');
        return hasRealItems ? { ...item, children: filtered } : null;
      }
      if (item.type === 'divider') return item;
      // Items with onClick handlers (no path) are always shown —
      // permission gating happens inside the handler / target.
      if (!item.path && typeof item.onClick === 'function') return item;
      return canAccess(userRole, pathnameOf(item.path)) ? item : null;
    })
    .filter(Boolean);

  // Find path from menu key
  const findPath = (items, key) => {
    for (const item of items) {
      if (item.key === key && item.path) return item.path;
      if (item.children) {
        const childPath = findPath(item.children, key);
        if (childPath) return childPath;
      }
    }
    return null;
  };

  // Handle menu click with Ctrl+Click for new tab
  const handleMenuClick = ({ key, domEvent }) => {
    const targetPath = findPath(menuItems, key);
    if (targetPath) {
      if (domEvent.ctrlKey || domEvent.metaKey || domEvent.button === 1) {
        window.open(targetPath, '_blank');
        domEvent.preventDefault();
        domEvent.stopPropagation();
      } else {
        navigate(targetPath);
      }
    }
  };

  // Get selected key based on current path. Compare only the pathname
  // portion so menu paths that pin a default ?mode= (e.g. DATA CHAT →
  // /data/chat?mode=chat) still match when the user navigates with a
  // different query string.
  const getSelectedKey = () => {
    for (const item of menuItems) {
      if (pathnameOf(item.path) === location.pathname) return [item.key];
      if (item.children) {
        const child = item.children.find(c => pathnameOf(c.path) === location.pathname);
        if (child) return [child.key];
      }
    }
    return ['1'];
  };

  // Theme mode groups
  const lightThemes = Object.entries(themes).filter(([, t]) => t.mode === 'light');
  const darkThemes = Object.entries(themes).filter(([, t]) => t.mode === 'dark');
  const currentMode = themes[currentTheme].mode;

  // User dropdown menu items
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: (
        <span>
          {userProfile?.display_name || (userEmail ? userEmail.split('@')[0] : 'User')}
          <span style={{ marginLeft: 6, opacity: 0.6, fontSize: 11, textTransform: 'capitalize' }}>
            · {userRole || 'user'}
          </span>
        </span>
      ),
      disabled: true,
    },
    { type: 'divider' },
    {
      key: 'theme',
      icon: <BgColorsOutlined />,
      label: 'Theme',
      onClick: () => setShowThemeModal(true),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: logout,
    },
  ];

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={true}
        width={64}
        collapsedWidth={64}
        style={{
          position: 'relative',
          height: '100vh',
          overflow: 'visible',
          background: 'var(--theme-sidebarGradient)',
          borderRight: '1px solid var(--theme-border)',
        }}
      >
        <div style={{
          paddingTop: '8px',
          paddingBottom: '6px',
          borderBottom: '1px solid var(--theme-border)',
          background: 'var(--theme-secondary)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          {/* AI ASSISTANT mini-logo */}
          <div
            title="AI ASSISTANT"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 46,
              height: 46,
            }}
          >
            <RobotOutlined style={{ fontSize: 34, color: '#ffffff' }} title="AI" />
          </div>
        </div>
        <Menu
          mode="inline"
          inlineCollapsed={true}
          selectedKeys={getSelectedKey()}
          style={{
            height: 'calc(100vh - 120px)',
            overflowY: 'auto',
            overflowX: 'visible',
            border: 'none',
            background: 'transparent',
            width: 64,
          }}
          onClick={handleMenuClick}
          items={menuItems}
        />
        {/* Profile section at bottom */}
        <div style={{
          position: 'absolute',
          bottom: 16,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderTop: '1px solid var(--theme-border)',
          paddingTop: '16px',
        }}>
          <Dropdown menu={{ items: userMenuItems }} placement="rightBottom" trigger={['click']}>
            <Avatar
              style={{ background: 'var(--theme-logoGradient)', cursor: 'pointer' }}
              icon={<UserOutlined />}
            />
          </Dropdown>
        </div>
      </Sider>
      {/* Theme Selection Modal */}
      <Modal
        title={<span><BgColorsOutlined style={{ marginRight: 8 }} />Select Theme</span>}
        open={showThemeModal}
        onCancel={() => setShowThemeModal(false)}
        footer={null}
        width={500}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <h4 style={{ marginBottom: 12 }}>Mode</h4>
            <Radio.Group
              value={currentMode}
              buttonStyle="solid"
              size="large"
              onChange={(e) => {
                const newMode = e.target.value;
                const firstThemeOfMode = newMode === 'light' ? lightThemes[0][0] : darkThemes[0][0];
                changeTheme(firstThemeOfMode);
              }}
            >
              <Radio.Button value="light">Light</Radio.Button>
              <Radio.Button value="dark">Dark</Radio.Button>
            </Radio.Group>
          </div>
          <div>
            <h4 style={{ marginBottom: 12 }}>Color</h4>
            <Radio.Group
              value={currentTheme}
              onChange={(e) => changeTheme(e.target.value)}
              style={{ width: '100%' }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {(currentMode === 'light' ? lightThemes : darkThemes).map(([key, theme]) => (
                  <Card
                    key={key}
                    size="small"
                    hoverable
                    onClick={() => changeTheme(key)}
                    style={{
                      cursor: 'pointer',
                      border: currentTheme === key ? `2px solid ${theme.primary}` : '1px solid #d9d9d9',
                      background: currentTheme === key ? theme.background : 'transparent',
                    }}
                  >
                    <Radio value={key} style={{ width: '100%' }}>
                      <Space>
                        <span style={{ fontWeight: currentTheme === key ? 600 : 400 }}>{theme.name}</span>
                        <div style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
                          <div style={{ width: 24, height: 24, borderRadius: 4, background: theme.primary, border: '1px solid rgba(0,0,0,0.1)' }} />
                          <div style={{ width: 24, height: 24, borderRadius: 4, background: theme.secondary, border: '1px solid rgba(0,0,0,0.1)' }} />
                          <div style={{ width: 24, height: 24, borderRadius: 4, background: theme.logoGradient, border: '1px solid rgba(0,0,0,0.1)' }} />
                        </div>
                      </Space>
                    </Radio>
                  </Card>
                ))}
              </Space>
            </Radio.Group>
          </div>
        </Space>
      </Modal>
      <Layout className="site-layout" style={{ height: '100vh' }}>
        <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', position: 'relative' }}>
          {/* Dashboard panel — rendered at layout level so it spans full height */}
          {isOnDataChat && <DashboardPanel />}
          <div
            className={
              isFloatingChat
                ? `chat-floating${floatingChatExpanded ? '' : ' chat-floating-collapsed'}`
                : ''
            }
            style={
              isFloatingChat
                ? { right: `${24 + chatOffset.x}px`, bottom: `${24 + chatOffset.y}px` }
                : { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }
            }
          >
            {/* Floating-chat header — only shown when chat is the floating overlay */}
            {isFloatingChat && floatingChatExpanded && (
              <div
                className="chat-floating-header"
                onMouseDown={(e) => beginDrag(e, 'chat')}
              >
                <span className="chat-floating-title">
                  <CommentOutlined style={{ marginRight: 6 }} />
                  Chat
                </span>
                <span style={{ display: 'inline-flex', gap: 4 }}>
                  <Button
                    type="text"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={handleNewChat}
                    aria-label="New chat"
                    title="New chat"
                    className="chat-floating-collapse-btn"
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<MinusOutlined />}
                    onClick={() => setFloatingChatExpanded(false)}
                    aria-label="Minimize chat"
                    className="chat-floating-collapse-btn"
                  />
                </span>
              </div>
            )}
            {/* Content toolbar — only shown on chat pages, hidden in floating mode */}
            {isOnChatPage && !isFloatingChat && (
              <div className="content-toolbar">
                {/* Left: Build dashboard button */}
                <div className="content-toolbar-left">
                  {isOnDataChat && !isDashboardOpen && (
                    <Button
                      type="primary"
                      icon={<RightOutlined />}
                      onClick={() => {
                        // Open dashboard list alongside chat (hybrid layout)
                        // rather than fullscreen — matches the welcome-card
                        // "Build a BI dashboard with me" affordance.
                        discardDraft?.();
                        navigate('/data/chat/dashboards?mode=hybrid');
                      }}
                      className="build-dashboard-btn"
                    >
                      Build your dashboard
                    </Button>
                  )}
                </div>
                {/* Center: Model selector + token quota */}
                <div className="content-toolbar-center">
                  <ModelSelector />
                  <TokenQuotaWidget quotaInfo={quotaInfo} size={44} strokeWidth={4} />
                </div>
                {/* Right: New Chat button */}
                <div className="content-toolbar-right">
                  <Button type="primary" icon={<CommentOutlined />} onClick={handleNewChat} size="large">
                    New Chat
                  </Button>
                </div>
              </div>
            )}
            <Content style={{ overflow: 'auto', height: isOnChatPage ? 'calc(100vh - 60px)' : '100vh', flex: 1, background: 'var(--theme-contentBg)' }}>
          <Routes>
            <Route path="/" element={<Navigate to="/data/chat?mode=chat" replace />} />
            <Route path="/multi_agent" element={HIDDEN_ROUTES.includes('/multi_agent') ? <Navigate to="/data/chat?mode=chat" replace /> : (canAccess(userRole, '/multi_agent') ? <Home /> : <Unauthorized />)} />
            <Route path="/manager/chat" element={HIDDEN_ROUTES.includes('/manager/chat') ? <Navigate to="/data/chat?mode=chat" replace /> : (canAccess(userRole, '/manager/chat') ? <ManagerChat registerChatReset={registerChatReset} /> : <Unauthorized />)} />
            <Route path="/manager/history" element={HIDDEN_ROUTES.includes('/manager/history') ? <Navigate to="/data/chat?mode=chat" replace /> : (canAccess(userRole, '/manager/history') ? <ManagerQAHistory /> : <Unauthorized />)} />

            <Route path="/data/chat" element={canAccess(userRole, '/data/chat') ? <DataChat registerChatReset={registerChatReset} /> : <Unauthorized />} />
            <Route path="/data/chat/dashboards" element={canAccess(userRole, '/data/chat') ? <DataChat registerChatReset={registerChatReset} /> : <Unauthorized />} />
            <Route path="/data/chat/dashboard/:dashboardUuid" element={canAccess(userRole, '/data/chat') ? <DataChat registerChatReset={registerChatReset} /> : <Unauthorized />} />
            <Route path="/data/history" element={<Navigate to="/data-gallery?tab=qa-history" replace />} />
            <Route path="/data/catalog-sync" element={canAccess(userRole, '/data/catalog-sync') ? <DataExplorer /> : <Unauthorized />} />
            <Route path="/data/graphql-builder" element={HIDDEN_ROUTES.includes('/data/graphql-builder') ? <Navigate to="/data/chat?mode=chat" replace /> : (canAccess(userRole, '/data/graphql-builder') ? <GraphQLBuilder /> : <Unauthorized />)} />
            <Route path="/data-gallery" element={canAccess(userRole, '/data-gallery') ? <DataGallery userRole={userRole} /> : <Unauthorized />} />
            <Route path="/data/ontology" element={canAccess(userRole, '/data/ontology') ? <Ontology /> : <Unauthorized />} />
            <Route path="/data/model" element={canAccess(userRole, '/data/model') ? <DataModel /> : <Unauthorized />} />

            <Route path="/knowledge/chat" element={HIDDEN_ROUTES.includes('/knowledge/chat') ? <Navigate to="/data/chat?mode=chat" replace /> : (canAccess(userRole, '/knowledge/chat') ? <KnowledgeChat registerChatReset={registerChatReset} /> : <Unauthorized />)} />
            <Route path="/knowledge/history" element={HIDDEN_ROUTES.includes('/knowledge/history') ? <Navigate to="/data/chat?mode=chat" replace /> : (canAccess(userRole, '/knowledge/history') ? <KnowledgeQAHistory /> : <Unauthorized />)} />
            <Route path="/knowledge/status" element={HIDDEN_ROUTES.includes('/knowledge/status') ? <Navigate to="/data/chat?mode=chat" replace /> : (canAccess(userRole, '/knowledge/status') ? <KnowledgeStatus /> : <Unauthorized />)} />

            <Route path="/admin/users" element={canAccess(userRole, '/admin/users') ? <UserManagement /> : <Unauthorized />} />
            <Route path="/admin/settings/data-access-groups" element={canAccess(userRole, '/admin/settings/data-access-groups') ? <DataAccessGroups /> : <Unauthorized />} />
            <Route path="/admin/audit/ai" element={canAccess(userRole, '/admin/audit/ai') ? <AIUsage /> : <Unauthorized />} />
            <Route path="/admin/audit/data" element={canAccess(userRole, '/admin/audit/data') ? <DataUsage /> : <Unauthorized />} />
            <Route path="/admin/audit/users" element={canAccess(userRole, '/admin/audit/users') ? <UserActivity /> : <Unauthorized />} />
            <Route path="/admin/settings/users" element={canAccess(userRole, '/admin/settings/users') ? <UserSettings /> : <Unauthorized />} />
            <Route path="/admin/settings/ai" element={canAccess(userRole, '/admin/settings/ai') ? <AISettings /> : <Unauthorized />} />
            <Route path="/admin/data-agent-telemetry" element={canAccess(userRole, '/admin/data-agent-telemetry') ? <DataAgentTelemetry /> : <Unauthorized />} />
            <Route path="*" element={<Navigate to="/data/chat?mode=chat" replace />} />
          </Routes>
            </Content>
          </div>
          {/* Floating chat FAB — only shown when dashboard is in fullscreen
              and the floating chat is collapsed. Click to re-expand. */}
          {isFloatingChat && !floatingChatExpanded && (
            <Button
              type="primary"
              shape="circle"
              size="large"
              icon={<CommentOutlined />}
              onMouseDown={(e) => beginDrag(e, 'fab')}
              onClick={() => setFloatingChatExpanded(true)}
              aria-label="Open chat"
              className="chat-floating-fab"
              style={{ right: `${24 + chatOffset.x}px`, bottom: `${24 + chatOffset.y}px` }}
            />
          )}
        </div>
      </Layout>
    </Layout>
  );
}

function AuthenticatedApp() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'transparent',
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Router>
        <Routes>
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route path="*" element={<AccessGate />} />
        </Routes>
        {/* Modal also mounted here so a 401 received during the brief
            window between expiry and the user reaching the login page
            still surfaces (rare, but cheap to cover). */}
        <SessionExpiredModal />
      </Router>
    );
  }
  return (
    <DashboardProvider>
      <Router>
        <AppContent />
        {/* Top-level overlay: appears when AuthContext.sessionExpired is
            true. Preserves the in-progress dashboard / chart editor /
            chat state behind a non-dismissible re-auth modal. */}
        <SessionExpiredModal />
      </Router>
    </DashboardProvider>
  );
}

function App() {
  // ModelProvider is intentionally nested *inside* AuthProvider so it can
  // re-run its `/models` fetch once the user is authenticated. If it sat
  // outside, the initial fetch would race the login flow, frequently fail
  // with 401, and the selector would silently disappear until a full page
  // reload.
  return (
    <ThemeProvider>
      <AntdThemeBridge>
        <AuthProvider>
          <ModelProvider>
            <AuthenticatedApp />
          </ModelProvider>
        </AuthProvider>
      </AntdThemeBridge>
    </ThemeProvider>
  );
}

// Bridges our ThemeContext (CSS variables) to Ant Design's algorithm so
// dark themes also recolor Ant components (tables, modals, popovers, …).
function AntdThemeBridge({ children }) {
  const { themes, currentTheme } = useTheme();
  const mode = themes[currentTheme]?.mode || 'light';
  const primary = themes[currentTheme]?.primary || '#0c80df';
  return (
    <ConfigProvider
      theme={{
        algorithm: mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: { colorPrimary: primary },
      }}
    >
      {children}
    </ConfigProvider>
  );
}

export default App;
